import {
  Alert,
  Flex,
  RadioGroup,
  RadioTile,
  Strong,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useField, useFormikContext } from 'formik';

import {
  AppointmentParticipantType,
  HomeOwnerStatus,
} from '@generated/gql/graphql';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

interface HomeOwnerSelectionProps {
  name: string;
  disabled: boolean;
}

interface HomeOwnerSelectionValues {
  homeOwnerSelection: HomeOwnerStatus;
}

const HomeOwnerSelection = ({ name, disabled }: HomeOwnerSelectionProps) => {
  const { submitCount, setFieldValue }: FormikProps<HomeOwnerSelectionValues> =
    useFormikContext();

  const [field, meta] = useField({
    name,
    validate: (value) => {
      if (!value) return 'Required';
    },
  });

  const userInformation = useUserInformation();
  const { appointment } = useAppointmentContext();

  // Only shown when every Partner in the appointment is cleared to have
  // insurance discussed in front of them. An empty list means nobody to warn
  // about, which also covers sales-assisted and unassisted baskets.
  const partners =
    appointment?.participants.filter(
      (participant) =>
        participant.type === AppointmentParticipantType.Partner ||
        participant.type === AppointmentParticipantType.SupportingPartner
    ) ?? [];

  const profileInsurancePermissisons =
    userInformation?.__typename === 'PartnerProfileInformation'
      ? userInformation?.canDiscussInsurance
      : false;

  // we only want to check the profile permissions, for currently
  // logged in partner, if there is no appointment
  const appointmentInsurancePermissions =
    partners.length > 0
      ? partners.every((partner) => partner.canDiscussInsurance)
      : profileInsurancePermissisons;

  const showPartnerInsuranceNotice =
    appointmentInsurancePermissions &&
    field.value === HomeOwnerStatus.Homeowner;

  return (
    <Flex spacing="lg" direction="column">
      <div className="Address-homeowner">
        <RadioGroup
          label="Are you the owner of this property?"
          direction="row"
          disabled={disabled}
          contentWidth="100%"
          onValueChange={(value) => setFieldValue(name, value)}
          validationStatus={
            meta.error && submitCount > 0 ? 'invalid' : undefined
          }
          validationText={meta.error ?? undefined}
          validationPlacement="bottom"
          {...field}
        >
          <RadioTile
            value={HomeOwnerStatus.Homeowner}
            label="Yes"
            id="AddressHomeOwnerYes"
            flexGrow={{ mobile: '1', tablet: '0' }}
          />
          <RadioTile
            value={HomeOwnerStatus.Tenant}
            label="No"
            id="AddressHomeOwnerNo"
            flexGrow={{ mobile: '1', tablet: '0' }}
          />
        </RadioGroup>
      </div>
      {showPartnerInsuranceNotice && (
        <Alert
          colorScheme="positive"
          title="Great news! 🎉"
          data-testid="homeowner-insurance-notice"
          text={
            <>
              As a home owner, you have the option to take our five-star Boiler
              and Home Cover Insurance, result! Your UW Partner would love to
              help, but insurance is serious stuff and there are rules about
              what they can discuss. <Strong>They can</Strong> talk you through
              energy, mobile, and broadband services and your overall bundle.{' '}
              <Strong>They can't</Strong> talk specifics about insurance or
              encourage you to take it, that's your call.
            </>
          }
        />
      )}
    </Flex>
  );
};

export default HomeOwnerSelection;
